import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Trophy, MapPin, Phone, Mail, Calendar as CalendarIcon, Facebook, Instagram, AlertTriangle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: false });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

export default function ClubPublicPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await publicApi.get(`/public/club/${slug}`);
      setData(data);
      if (data.team?.name) document.title = `${data.team.name} · Calcio Club`;
    } catch (e) {
      setError(e?.response?.data?.detail || "Pagina non disponibile");
    }
    setLoading(false);
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento...</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="p-10 max-w-md text-center">
        <AlertTriangle size={40} className="mx-auto text-red-500 mb-3" />
        <h2 className="font-heading text-2xl font-bold">Sito non disponibile</h2>
        <p className="text-slate-500 mt-2">{error}</p>
      </Card>
    </div>
  );

  const t = data.team;
  const sponsors = data.sponsors || [];
  const mains = sponsors.filter((s) => s.visibility === "main");
  const others = sponsors.filter((s) => s.visibility !== "main");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white" style={{ backgroundColor: t.color || "#0f172a" }}>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-5 flex-wrap">
            {t.logo_url ? (
              <img src={t.logo_url} alt={t.name} className="h-24 w-24 rounded-2xl object-cover shrink-0 bg-white p-1" />
            ) : (
              <div className="h-24 w-24 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
                <Trophy size={42} className="text-white/80" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider opacity-80">{t.category} · {t.season}</div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold mt-1">{t.legal_name || t.name}</h1>
              {t.description && <p className="text-white/80 mt-3 max-w-2xl">{t.description}</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-10">
        {/* Main sponsors */}
        {mains.length > 0 && (
          <section>
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">Main sponsor</h2>
            <div className="flex flex-wrap gap-6 items-center">
              {mains.map((s) => (
                <a key={s.name} href={s.website || "#"} target="_blank" rel="noreferrer" className="block">
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.name} className="h-20 object-contain" />
                  ) : (
                    <span className="text-2xl font-heading font-bold text-slate-700">{s.name}</span>
                  )}
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Next matches */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
            <CalendarIcon size={20} className="text-sky-500" /> Prossime partite
          </h2>
          {data.next_matches?.length === 0 ? (
            <Card className="p-6 text-center text-slate-400">Nessuna partita in programma.</Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.next_matches.map((m) => (
                <Card key={m.id} className="p-4 border-slate-200">
                  <div className="text-xs text-sky-600 font-semibold">{fmtDate(m.match_date)}{m.meeting_time && ` · ${m.meeting_time}`}</div>
                  <div className="font-heading font-bold text-lg mt-1">{t.name} vs {m.opponent || m.title}</div>
                  {m.location && <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={11} />{m.location}</div>}
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Recent results */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-4">Risultati recenti</h2>
          {data.recent_results?.length === 0 ? (
            <Card className="p-6 text-center text-slate-400">Nessun risultato ancora.</Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {data.recent_results.map((r) => {
                const win = r.final_score_us > r.final_score_them;
                const draw = r.final_score_us === r.final_score_them;
                return (
                  <Card key={r.id} className={`p-4 border-2 ${win ? "border-emerald-200" : draw ? "border-amber-200" : "border-red-200"}`}>
                    <div className="text-xs text-slate-500">{fmtDate(r.match_date)}</div>
                    <div className="font-semibold mt-1 truncate">vs {r.opponent}</div>
                    <div className={`text-2xl font-bold mt-1 ${win ? "text-emerald-600" : draw ? "text-amber-600" : "text-red-600"}`}>
                      {r.final_score_us} - {r.final_score_them}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* Other sponsors */}
        {others.length > 0 && (
          <section>
            <h2 className="font-heading text-2xl font-bold mb-4">I nostri partner</h2>
            <div className="flex flex-wrap gap-6 items-center justify-center bg-white rounded-2xl p-6 border border-slate-200">
              {others.map((s) => (
                <a key={s.name} href={s.website || "#"} target="_blank" rel="noreferrer" className="opacity-80 hover:opacity-100 transition">
                  {s.logo_url ? <img src={s.logo_url} alt={s.name} className="h-12 object-contain grayscale hover:grayscale-0" /> : <span className="text-slate-600 font-semibold">{s.name}</span>}
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Contacts */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-4">Contatti</h2>
          <Card className="p-6 border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 text-sm">
              {t.address && <div className="flex gap-2"><MapPin size={16} className="text-sky-500 shrink-0 mt-0.5" /><span>{t.address}</span></div>}
              {t.phone && <div className="flex gap-2"><Phone size={16} className="text-sky-500 shrink-0 mt-0.5" /><a href={`tel:${t.phone}`} className="hover:text-sky-600">{t.phone}</a></div>}
              {t.email && <div className="flex gap-2"><Mail size={16} className="text-sky-500 shrink-0 mt-0.5" /><a href={`mailto:${t.email}`} className="hover:text-sky-600 break-all">{t.email}</a></div>}
            </div>
            <div className="flex flex-col gap-2">
              {t.social_facebook && <a href={t.social_facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700"><Facebook size={16} /> Facebook</a>}
              {t.social_instagram && <a href={t.social_instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-pink-600 hover:text-pink-700"><Instagram size={16} /> Instagram</a>}
            </div>
          </Card>
        </section>

        <p className="text-center text-xs text-slate-400 pt-6 border-t border-slate-200">
          {t.legal_name || t.name} · Powered by Calcio Club Manager
        </p>
      </div>
    </div>
  );
}
